import { useNavigate } from 'react-router-dom'
import { ExternalLink, RefreshCw } from 'lucide-react'
import {
  Badge,
  Button,
  ErrorState,
  LoadingState,
  Modal,
  SimpleTable,
  StatusBadge,
  useToast,
} from '@/components/ui'
import { useBillingRun, useRetrySapItem } from '../api/billingApi.ts'
import type { BillingItem } from '../types.ts'

interface BillingRunDetailDrawerProps {
  runId: string | null
  onClose: () => void
}

function fmtCurrency(cents: number, currency = 'EUR'): string {
  return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency })
}

function fmtMonth(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('de-DE', { year: 'numeric', month: 'long' })
}

export function BillingRunDetailDrawer({ runId, onClose }: BillingRunDetailDrawerProps) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const open = Boolean(runId)
  const { data: run, isLoading, error, refetch } = useBillingRun(runId ?? undefined)
  const retry = useRetrySapItem()

  function handleRetry(itemId: string) {
    retry.mutate(itemId, {
      onSuccess: (r) => {
        if (r.ok) {
          toast({
            title: 'SAP-Übergabe erfolgreich',
            description: r.sapDocEntry ? `DocEntry ${r.sapDocEntry}` : undefined,
            tone: 'success',
          })
        } else {
          toast({
            title: 'SAP-Übergabe fehlgeschlagen',
            description: r.message,
            tone: 'danger',
          })
        }
      },
      onError: (err) =>
        toast({
          title: 'Retry fehlgeschlagen',
          description: err instanceof Error ? err.message : 'Unbekannter Fehler',
          tone: 'danger',
        }),
    })
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose() }}
      size="xl"
      title={run ? `Abrechnungslauf ${fmtMonth(run.periodStart)}` : 'Abrechnungslauf'}
      description={
        run ? (
          <span className="inline-flex items-center gap-2">
            <StatusBadge status={`billing_${run.status}`} />
            <span className="text-caption text-gray-500">
              {run.itemsBilled} Posten · {run.contractsBilled} Verträge · Summe{' '}
              {fmtCurrency(run.totalAmountCents, run.currency)}
            </span>
          </span>
        ) : null
      }
      footer={
        <Button variant="secondary" size="sm" onClick={onClose}>
          Schließen
        </Button>
      }
    >
      {isLoading ? <LoadingState label="Lade Lauf …" /> : null}
      {error ? (
        <ErrorState
          description="Lauf konnte nicht geladen werden."
          details={error instanceof Error ? error.message : String(error)}
          onRetry={() => void refetch()}
        />
      ) : null}
      {run ? (
        <div className="max-h-[60vh] overflow-auto">
          <SimpleTable>
            <SimpleTable.Head>
              <SimpleTable.Tr>
                <SimpleTable.Th>Vertrag</SimpleTable.Th>
                <SimpleTable.Th>Position</SimpleTable.Th>
                <SimpleTable.Th className="text-right">Menge</SimpleTable.Th>
                <SimpleTable.Th className="text-right">Einzelpreis</SimpleTable.Th>
                <SimpleTable.Th className="text-right">Summe</SimpleTable.Th>
                <SimpleTable.Th>SAP</SimpleTable.Th>
                <SimpleTable.Th>Hinweis</SimpleTable.Th>
              </SimpleTable.Tr>
            </SimpleTable.Head>
            <SimpleTable.Body>
              {run.items.map((it: BillingItem) => (
                <SimpleTable.Tr key={it.id}>
                  <SimpleTable.Td>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-brand-700 hover:underline"
                      onClick={() => navigate(`/vertraege/${it.contractId}`)}
                    >
                      <ExternalLink className="w-3 h-3" />
                      {it.contractNumber ?? '—'}
                    </button>
                  </SimpleTable.Td>
                  <SimpleTable.Td>{it.description ?? '—'}</SimpleTable.Td>
                  <SimpleTable.Td className="text-right tabular-nums">
                    {(it.quantityX1000 / 1000).toLocaleString('de-DE')}
                  </SimpleTable.Td>
                  <SimpleTable.Td className="text-right tabular-nums">
                    {fmtCurrency(it.unitPriceCents, it.currency)}
                  </SimpleTable.Td>
                  <SimpleTable.Td className="text-right tabular-nums font-medium">
                    {fmtCurrency(it.totalCents, it.currency)}
                  </SimpleTable.Td>
                  <SimpleTable.Td>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={`sap_${it.sapStatus}`} />
                      {it.sapDocEntry ? (
                        <span className="text-caption text-gray-500">#{it.sapDocEntry}</span>
                      ) : null}
                      {(it.sapStatus === 'pending' || it.sapStatus === 'error') && run.status !== 'draft' ? (
                        <Button
                          variant="ghost"
                          size="xs"
                          leadingIcon={<RefreshCw className="w-3 h-3" />}
                          loading={retry.isPending}
                          onClick={() => handleRetry(it.id)}
                        >
                          Retry
                        </Button>
                      ) : null}
                    </div>
                    {it.sapErrorMessage ? (
                      <p className="text-caption text-red-600 mt-0.5 max-w-xs truncate" title={it.sapErrorMessage}>
                        {it.sapErrorMessage}
                      </p>
                    ) : null}
                  </SimpleTable.Td>
                  <SimpleTable.Td>
                    {it.pricingNote ? <Badge variant="neutral" size="sm">{it.pricingNote}</Badge> : null}
                  </SimpleTable.Td>
                </SimpleTable.Tr>
              ))}
            </SimpleTable.Body>
          </SimpleTable>
          {run.items.length === 0 ? (
            <p className="text-caption text-gray-500 px-3 py-4">Keine Posten in diesem Lauf.</p>
          ) : null}
        </div>
      ) : null}
    </Modal>
  )
}
